import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"

import { replaceHistoryState } from "client/utils"
import {
    ModalParams,
    ModalWithUrl,
    ModalWithContent,
    StoreType,
    closeModal,
} from "client/store"
import { Content } from "client/parts/content"
import { CrossIcon } from "client/components"
import {
    ModalContainer,
    ModalWrapper,
    ModalContentWrapper,
    ModalContent,
    CloseButtonWrapper,
    CloseButton,
    CloseButtonLabel,
} from "./styled"

const modalTimeout = 300
const modalMaxNested = 4

let clickStart: number = null
let clickEnd: number = null

const Modal = () => {
    const [modals, setModals] = useState<ModalParams[]>([])
    const [adding, setAdding] = useState<number>(null)
    const [closing, setClosing] = useState<number>(null)
    const [animateBackground, setAnimateBackground] = useState(false)

    const urlOrigin = useSelector((state: StoreType) => state.system.urlOrigin)
    const storeModals = useSelector((state: StoreType) => state.system.modals) as ModalParams[]
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    useEffect(() => {
        // close modal
        if (modals.length > storeModals.length) {
            const baseWindow: ModalWithUrl = { url: urlOrigin }
            const index = modals.length - 1
            const cur = modals[index]
            const prev = index > 0 ? modals[index - 1] : baseWindow
            const curModal = cur as ModalWithUrl
            const prevModal = prev as ModalWithUrl

            // update URL only if it was rewritten by the last modal
            if (curModal.url && prevModal.url) replaceHistoryState(prevModal.url)

            // execute action onClose
            if (curModal.onClose) curModal.onClose()

            setClosing(index) // start closing animation
            setAnimateBackground(index === 0)

            setTimeout(() => {
                setModals(storeModals)
                setClosing(null)
            }, modalTimeout) // wait for closing animation
        }

        // add new modal
        if (modals.length < storeModals.length) {
            const index = storeModals.length - 1
            const nextModal = storeModals[index] as ModalWithUrl

            if (nextModal.url) replaceHistoryState(nextModal.url)
            setAdding(index) // render new modal
            setAnimateBackground(index === 0)
            setModals(storeModals)

            setTimeout(() => {
                setAdding(null)
            }, 100) // start opening animation after new modal is rendered
        }

        // update url
        if (modals.length === storeModals.length) {
            setModals(storeModals)
        }
    }, [storeModals])

    let hidden: number = null
    if (adding !== null) hidden = adding
    if (closing !== null) hidden = closing

    return modals.length && (
        <ModalContainer>
            {
                modals.map((params, index) => {
                    let key: string
                    let renderContent: JSX.Element
                    const modalWithUrl = params as ModalWithUrl
                    if (modalWithUrl.url) {
                        key = `${index}_${modalWithUrl.url}`
                        renderContent = <Content />
                    } else {
                        const modalWithContent = params as ModalWithContent
                        key = `${index}_${modalWithContent.key}`
                        renderContent = modalWithContent.content
                    }

                    const show = index !== hidden
                    const showBackground = hidden === null
                        ? index === modals.length - 1
                        : index === modals.length - 2

                    let showShadow = !(index > modalMaxNested && index < modals.length - 1)
                    showShadow = (hidden && index === hidden - 1) || showShadow

                    const closeButtonOffsetMultiplier = closing === null
                        ? modals.length - index - 1
                        : (
                            closing === index
                                ? modals.length - index - 1
                                : modals.length - index - 2
                        )

                    return (
                        <ModalWrapper
                            key={key}
                            show={show}
                            showBackground={showBackground}
                            animateBackground={animateBackground}
                            timeout={modalTimeout}
                            onMouseUp={() => {
                                if (hidden !== null) return
                                if (clickStart === null && clickEnd === null) {
                                    dispatch(closeModal())
                                }
                                clickStart = null
                                clickEnd = null
                            }}
                        >
                            <ModalContentWrapper
                                show={show}
                                offsetMultiplier={index > modalMaxNested ? modalMaxNested : index}
                                timeout={modalTimeout}
                            >
                                <CloseButtonWrapper
                                    offsetMultiplier={closeButtonOffsetMultiplier}
                                    timeout={modalTimeout}
                                >
                                    <CloseButton show={
                                        closing === null
                                            ? index === modals.length - 1
                                            : index === modals.length - 2
                                    }
                                    >
                                        <CrossIcon />
                                    </CloseButton>
                                    <CloseButtonLabel>
                                        {lang.close}
                                    </CloseButtonLabel>
                                </CloseButtonWrapper>
                                <ModalContent
                                    showShadow={showShadow}
                                    onMouseDown={() => { clickStart = 1 }}
                                    onMouseUp={() => { clickEnd = 1 }}
                                >
                                    {renderContent}
                                </ModalContent>
                            </ModalContentWrapper>
                        </ModalWrapper>
                    )
                })
            }
        </ModalContainer>
    )
}
export default Modal
